import { Money, SavingsRateInfo } from '@varo/graphql-schema-types';
import { isValid, parseISO } from 'date-fns';
import { formatCurrency } from 'shared/utils/string/currency';
import { formatDate, formatDateWithSlash } from 'shared/utils/string/date';
import { Maybe } from 'shared/utils/types';

export interface SavingsRates {
  standardApy: string;
  elevatedApy: string;
  balanceCap: string;
  compactBalanceCap: string;
  depositThreshold: string;
  asOfDate: string;
}

const USD_CURRENCY = 'USD';
const BPS_PER_PERCENT = 100;

type MoneyAmount = Maybe<Pick<Money, 'amount' | 'currency'>>;

interface SavingsRateInfoInput extends Pick<
  SavingsRateInfo,
  'standardApyBps' | 'elevatedApyBps'
> {
  asOfDate?: Maybe<string>;
  elevatedBalanceCap?: MoneyAmount;
  depositThreshold?: MoneyAmount;
}

const positiveUsdAmount = (money: MoneyAmount): number | undefined => {
  if (money?.currency !== USD_CURRENCY) {
    return undefined;
  }
  const amount = Number(money.amount);
  return Number.isFinite(amount) && amount > 0 ? amount : undefined;
};

export const formatCompactUsdAmount = (
  money: MoneyAmount,
): string | undefined => {
  const amount = positiveUsdAmount(money);
  return amount === undefined
    ? undefined
    : formatCurrency(amount, !Number.isInteger(amount));
};

export const formatBalanceCap = (money: MoneyAmount): string | undefined => {
  const amount = positiveUsdAmount(money);
  return amount === undefined ? undefined : formatCurrency(amount, true);
};

export const formatApyBps = (bps: Maybe<number>): string | undefined =>
  typeof bps === 'number' && Number.isInteger(bps) && bps > 0
    ? `${(bps / BPS_PER_PERCENT).toFixed(2)}%`
    : undefined;

export const formatAsOfDate = (
  asOfDate: Maybe<string>,
  fallbackDate: Date,
): string => {
  const serverDate = asOfDate ? parseISO(asOfDate) : undefined;
  const date = serverDate && isValid(serverDate) ? serverDate : fallbackDate;
  return formatDateWithSlash(formatDate(date, 'MM/dd/yyyy'));
};

export const toSavingsRates = (
  info: Maybe<SavingsRateInfoInput>,
  fetchedAt: Date,
): SavingsRates | undefined => {
  const standardApy = formatApyBps(info?.standardApyBps);
  const elevatedApy = formatApyBps(info?.elevatedApyBps);
  const balanceCap = formatBalanceCap(info?.elevatedBalanceCap);
  const compactBalanceCap = formatCompactUsdAmount(info?.elevatedBalanceCap);
  const depositThreshold = formatCompactUsdAmount(info?.depositThreshold);

  if (
    !standardApy ||
    !elevatedApy ||
    !balanceCap ||
    !compactBalanceCap ||
    !depositThreshold
  ) {
    return undefined;
  }
  return {
    standardApy,
    elevatedApy,
    balanceCap,
    compactBalanceCap,
    depositThreshold,
    asOfDate: formatAsOfDate(info?.asOfDate, fetchedAt),
  };
};
